/**
 * @fileoverview VendingCompCard — staff-side complimentary vending credit
 * (mig 0064). Lists the vending machines at a location (GET
 * /api/locations/:id/vending-machines), offers an amount picker that
 * respects each machine's creditStepCents / maxCreditCents (presets when
 * configured), and POSTs { machineId, amountCents } to the given comp
 * endpoint:
 *   owner     → /api/owner/vending/comp
 *   attendant → /api/attendants/vending/comp
 * Renders nothing when the location has no machines.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { apiGet, apiPost } from '../api/client';
import Card from './Card';
import Button from './Button';
import { useToast } from './Toast';
import { useTheme } from '../context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius, type ColorTheme } from '../utils/theme';
import { rs } from '../utils/responsive';

interface VendingMachineRow {
  id: string;
  name: string;
  creditStepCents: number;
  presets: number[] | null;
  maxCreditCents: number;
  isActive: boolean;
}

interface Props {
  /** Location whose machines to list; null/'' renders nothing. */
  locationId: string | null;
  /** POST target: /api/owner/vending/comp or /api/attendants/vending/comp. */
  compEndpoint: string;
}

/** Amount choices for a machine: presets when configured, else $1/$2/$3
 *  (only step multiples within the max), else the first few raw steps. */
function amountOptionsFor(m: VendingMachineRow): number[] {
  const step = m.creditStepCents || 25;
  if (m.presets?.length) {
    return m.presets.filter(p => p <= m.maxCreditCents && p % step === 0);
  }
  const defaults = [100, 200, 300].filter(a => a <= m.maxCreditCents && a % step === 0);
  if (defaults.length > 0) return defaults;
  const fallback: number[] = [];
  for (let a = step; a <= m.maxCreditCents && fallback.length < 3; a += step) fallback.push(a);
  return fallback;
}

export default function VendingCompCard({ locationId, compEndpoint }: Props) {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();

  const [machines, setMachines] = useState<VendingMachineRow[]>([]);
  const [selectedMachineId, setSelectedMachineId] = useState<string | null>(null);
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setMachines([]);
    setSelectedMachineId(null);
    setSelectedAmount(null);
    if (!locationId) return;
    const ac = new AbortController();
    apiGet<VendingMachineRow[]>(`/api/locations/${locationId}/vending-machines`)
      .then(rows => {
        if (ac.signal.aborted) return;
        const active = rows.filter(m => m.isActive);
        setMachines(active);
        if (active.length === 1) setSelectedMachineId(active[0].id);
      })
      .catch(() => { /* no machines / fetch failed — card stays hidden */ });
    return () => ac.abort();
  }, [locationId]);

  const selectedMachine = machines.find(m => m.id === selectedMachineId) ?? null;
  const amountOptions = useMemo(
    () => (selectedMachine ? amountOptionsFor(selectedMachine) : []),
    [selectedMachine],
  );

  // Keep the picked amount valid for the currently selected machine.
  useEffect(() => {
    if (selectedAmount != null && amountOptions.includes(selectedAmount)) return;
    setSelectedAmount(amountOptions.length > 0 ? amountOptions[0] : null);
  }, [amountOptions]);

  if (!locationId || machines.length === 0) return null;

  const handleComp = async () => {
    if (!selectedMachine || selectedAmount == null) return;
    setSending(true);
    try {
      const res = await apiPost<{ transactionId: string; creditCents: number; machineName: string }>(
        compEndpoint,
        { machineId: selectedMachine.id, amountCents: selectedAmount },
      );
      toast.show({
        type: 'success',
        title: 'Credit Sent',
        message: `$${(res.creditCents / 100).toFixed(2)} pushed to ${res.machineName || selectedMachine.name} — make the selection at the machine.`,
      });
    } catch (err: any) {
      // 502 CONTROLLER_OFFLINE etc. — surface the server's message.
      toast.show({ type: 'error', title: 'Failed', message: err.message || 'Could not credit the machine' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Card style={styles.card}>
      <View style={styles.heroRow}>
        <Ionicons name="fast-food" size={24} color={colors.primary} />
        <Text style={styles.title}>Vending</Text>
      </View>
      <Text style={styles.subtitle}>Push complimentary vending credit to a machine</Text>

      <Text style={styles.label}>Machine</Text>
      <View style={styles.chipRow}>
        {machines.map(m => (
          <TouchableOpacity
            key={m.id}
            style={[styles.chip, selectedMachineId === m.id && styles.chipSelected]}
            onPress={() => setSelectedMachineId(m.id)}
            accessibilityRole="radio"
            accessibilityState={{ selected: selectedMachineId === m.id, checked: selectedMachineId === m.id }}
          >
            <Text style={[styles.chipText, selectedMachineId === m.id && styles.chipTextSelected]}>{m.name}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {selectedMachine && (
        <>
          <Text style={styles.label}>Amount</Text>
          <View style={styles.chipRow}>
            {amountOptions.map(a => (
              <TouchableOpacity
                key={a}
                style={[styles.amountBtn, selectedAmount === a && styles.amountBtnSelected]}
                onPress={() => setSelectedAmount(a)}
                accessibilityRole="radio"
                accessibilityState={{ selected: selectedAmount === a, checked: selectedAmount === a }}
                accessibilityLabel={`$${(a / 100).toFixed(2)} vending credit`}
              >
                <Text style={[styles.amountText, selectedAmount === a && styles.amountTextSelected]}>
                  ${(a / 100).toFixed(2)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
          <Text style={styles.hint}>
            Steps of ${(selectedMachine.creditStepCents / 100).toFixed(2)} · max ${(selectedMachine.maxCreditCents / 100).toFixed(2)}
          </Text>
        </>
      )}

      <Button
        title={selectedAmount != null ? `Send $${(selectedAmount / 100).toFixed(2)} Credit` : 'Select an Amount'}
        onPress={handleComp}
        loading={sending}
        disabled={!selectedMachine || selectedAmount == null}
        size="lg"
        style={{ marginTop: spacing.md }}
        icon={<Ionicons name="fast-food" size={20} color={colors.white} />}
      />
    </Card>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  card: { marginBottom: spacing.lg },
  heroRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.xs },
  title: { fontSize: fontSize.lg, fontWeight: fontWeight.bold as any, color: c.text },
  subtitle: { fontSize: fontSize.sm, color: c.textSecondary, marginBottom: spacing.md },
  label: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold as any, color: c.text, marginBottom: spacing.xs, marginTop: spacing.sm },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  chip: {
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    borderRadius: borderRadius.md, borderWidth: 1.5,
    borderColor: c.border, backgroundColor: c.surfaceSecondary,
  },
  chipSelected: { borderColor: c.primary, backgroundColor: c.primaryBg },
  chipText: { fontSize: fontSize.sm, fontWeight: fontWeight.medium as any, color: c.text },
  chipTextSelected: { color: c.primary, fontWeight: fontWeight.semibold as any },
  amountBtn: {
    minWidth: rs(72), paddingVertical: spacing.sm, paddingHorizontal: spacing.md,
    borderRadius: borderRadius.md, borderWidth: 1.5, borderColor: c.border,
    alignItems: 'center', backgroundColor: c.surfaceSecondary,
  },
  amountBtnSelected: { borderColor: c.primary, backgroundColor: c.primaryBg },
  amountText: { fontSize: fontSize.md, fontWeight: fontWeight.bold as any, color: c.text },
  amountTextSelected: { color: c.primary },
  hint: { fontSize: fontSize.xs, color: c.textLight, marginTop: spacing.xs },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
